import { useState, useEffect, useCallback } from "react";
import { usePostMessage } from "./usePostMessage";
import { ReadyMessage, RefreshResultMessage } from "@/services/postMessage/types";
import { recoverSession } from "@/utils/tokenManager";

export const AUTH_STATUS = {
  INITIALIZING: "INITIALIZING",
  WAITING: "WAITING",
  AUTHENTICATING: "AUTHENTICATING",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
} as const;

export type AuthStatus = (typeof AUTH_STATUS)[keyof typeof AUTH_STATUS];

export interface UseEmbedAuthOptions {
  backendUrl: string;
  allowedOrigins?: string[];
  onSuccess?: (data: { appId?: string; deploymentId?: string; viewId?: string }) => void;
  onError?: (error: string) => void;
}

export const useEmbedAuth = (options: UseEmbedAuthOptions) => {
  const [status, setStatus] = useState<AuthStatus>(AUTH_STATUS.INITIALIZING);
  const [error, setError] = useState<string>("");

  const service = usePostMessage({
    allowedOrigins: options.allowedOrigins,
    enabled: true,
  });

  const sendReady = useCallback(() => {
    if (!service) {
      console.warn("[useEmbedAuth] Service not ready, cannot send READY");
      return;
    }

    const message: ReadyMessage = {
      type: "READY",
      timestamp: Date.now(),
    };

    console.log("[useEmbedAuth] Sending READY message");
    service.send(message);
  }, [service]);

  useEffect(() => {
    if (!service) return;

    const refreshResultHandler = (message: RefreshResultMessage) => {
      setStatus(AUTH_STATUS.SUCCESS);
      setError("");
      if (options.onSuccess) {
        options.onSuccess({
          appId: message.appId,
          deploymentId: message.deploymentId,
          viewId: message.viewId,
        });
      }
    };

    const unsub = service.subscribe("REFRESH_RESULT", refreshResultHandler);
    return () => unsub();
  }, [service, options.onSuccess]);

  useEffect(() => {
    if (!service) return;

    let cancelled = false;

    const init = async () => {
      const existingSession = await recoverSession();

      if (cancelled) return;
      if (existingSession?.appId && existingSession?.deploymentId) {
        setStatus(AUTH_STATUS.SUCCESS);
        const navigationUrl = `/#/_/app/${existingSession.appId}/${existingSession.deploymentId}/`;
        globalThis.location.href = navigationUrl;
        return;
      }
      if (existingSession) {
        setStatus(AUTH_STATUS.SUCCESS);
      } else {
        setStatus(AUTH_STATUS.WAITING);
      }
      sendReady();
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [service, sendReady]);

  return {
    status,
    error,
    sendReady,
    isReady: status === AUTH_STATUS.WAITING,
    isAuthenticating: status === AUTH_STATUS.AUTHENTICATING,
    isAuthenticated: status === AUTH_STATUS.SUCCESS,
    hasError: status === AUTH_STATUS.ERROR,
  };
};
